import { Observable, NEVER, interval, of } from 'rxjs';
import { tap, switchMap } from 'rxjs/operators';

import { Injectable } from '@angular/core';

import { SnAudit, SnAuditOperation, SnCamera, SnContentType } from '@lib/api/entities';
import { SnAuditService } from '@lib/api/services';

@Injectable()
export class SnCameraAuditService {
  private readonly _collection = new Map<string, SnAudit>();

  constructor(private readonly _auditService: SnAuditService) {}

  public isPlaying(id: string): boolean {
    return this._collection.has(id);
  }

  public startPlaying(
    camera: SnCamera,
    operation: SnAuditOperation.ViewLive | SnAuditOperation.ViewRecord,
  ): Observable<SnAudit[]> {
    const payload: any = {
      object: {
        id: camera.id,
        name: camera.name,
        contentType: SnContentType.Camera,
      },
      operation,
    };
    if (camera.incidentId) {
      payload.detail = {};
      payload.detail.incidentId = camera.incidentId;
    }
    if (this._collection.has(camera.id)) {
      return of([this._collection.get(camera.id)]);
    }
    return this._auditService.createAudit([payload]).pipe(
      tap((audits: SnAudit[]) => {
        audits.forEach((audit) => this._collection.set(camera.id, audit));
      }),
    );
  }

  public stopPlaying(id: string): Observable<SnAudit> {
    return this._updateCameraAudit(id).pipe(tap(() => this._collection.delete(id)));
  }

  public pingPlay(id: string): Observable<SnAudit> {
    return this._updateCameraAudit(id).pipe(
      tap((targetAudit) => this._collection.set(id, targetAudit)),
    );
  }

  public initAuditListener(): Observable<SnAudit[]> {
    return interval(60000).pipe(switchMap(() => this._updateAudits()));
  }

  private _updateAudits(): Observable<SnAudit[]> {
    if (this._collection.size === 0) {
      return of([]);
    }
    return this._auditService.bulkStatusUpdate([...this._collection.values()]);
  }

  private _updateCameraAudit(id: string): Observable<SnAudit> {
    if (!this.isPlaying(id)) {
      return NEVER;
    }

    const audit = this._collection.get(id);
    if (!audit.id) {
      return NEVER;
    }
    return this._auditService.updateAuditStatus(audit.id);
  }
}
